import React, {  } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";

import { Tabs } from "@nokia-csf-uxr/ccfk";
import { Tab } from "@nokia-csf-uxr/ccfk/Tabs";

import LayoutGridIcon from "@nokia-csf-uxr/ccfk-assets/latest/LayoutGridIcon";
import EditIcon from "@nokia-csf-uxr/ccfk-assets/latest/EditIcon";
import ListIcon from "@nokia-csf-uxr/ccfk-assets/latest/ListIcon";
import DeleteIcon from "@nokia-csf-uxr/ccfk-assets/latest/DeleteIcon";
import DuplicateIcon from "@nokia-csf-uxr/ccfk-assets/latest/DuplicateIcon";
import { FILTER_TOOLBAR_TABS, OFFER_STTATUS } from "../utils/consstants";
import { RootState } from "Store/mainStore";
import { setProperty } from "../store/dashboardSlice";
import MoreActionButtons from "Components/common/MoreActionButtons/MoreActionButtons";
import DATAMART_API from "Constants/services/datamart";
import { setError, setStateRestoreFlag } from "Store/general/auth";
import DATAMART_SERVICES from "Constants/services/datamart_services";
import { useNavigate } from "react-router-dom";
import DataGridHeaderToolTip from "Components/Elements/DataGridHeaderToolTip";
import CONFIG_API from "Constants/services/configurator";
import { useSolutionLoad } from "Components/Content/configurationArea/Configurator/hooks/useSolutionLoad";
import { initialData } from "Store/contentState/configuration_stepperSlice";
import { initialData as dynamicInitialState } from "Store/contentState/configurationArea/dynamicSlice";

/*
 * Layout tabs for the dashboard.
 */
const LAYOUT_TAB = [
  {
    id: "grid",
    name: "Grid View",
    icon: <LayoutGridIcon />,
  },
  {
    id: "list",
    name: "List View",
    icon: <ListIcon />,
  },
];

const dataGridHeaderToolTip = (params) => {
  return (
    <DataGridHeaderToolTip title={params.value}></DataGridHeaderToolTip>
  );
}

const dataGridToolTipOptions = {
  tooltipComponent: dataGridHeaderToolTip,
}

// Dashboard grid columns configuration.
export const dashboardGidColumns = [
  {
    headerName: "Status",
    field: "offerStatus",
    flex: 1.5,
    cellRenderer: (params) => {
      return getStatusLabel(params.value);
    },
    cellStyle: (param) => {
      return {
        borderLeft:
          "solid var(--g-data-grid-kpi-status-bar-width) !important",
        borderLeftColor: `${getStatusColor(param.value)} !important`,
      };
    },
    headerTooltip: "Status",
    ...dataGridToolTipOptions
  },
  { headerName: "Offer ID", field: "offer_id", flex: 3, headerTooltip: "Offer ID", ...dataGridToolTipOptions },
  { headerName: "sCRM ID", field: "sCRMID", flex: 1.5, headerTooltip: "sCRM ID", ...dataGridToolTipOptions },
  { headerName: "Created Date", field: "createdAt", flex: 2, headerTooltip: "Created Date", ...dataGridToolTipOptions },
  { headerName: "Case name", field: "caseName", flex: 3, headerTooltip: "Case name", ...dataGridToolTipOptions },
  { headerName: "Account", field: "account_name", flex: 2, headerTooltip: "Account", ...dataGridToolTipOptions },
  { headerName: "Owner", field: "owner", flex: 2.5, headerTooltip: "Owner", ...dataGridToolTipOptions },
  { headerName: "Price", field: "totalPrice", flex: 1.5, headerTooltip: "Price", ...dataGridToolTipOptions },
  { headerName: "Duration", field: "offerAgeInDays", flex: 1.5, headerTooltip: "Duration", ...dataGridToolTipOptions },
];

/**
 * Get the status color based on the status
 * @param {string} status
 * @returns {string} color
 */
export const getStatusColor = (status) => {
  const statusItem = OFFER_STTATUS.find((item) => item.id === status);
  if (statusItem) {
    return statusItem.color;
  }
  return "var(--color-status-fault)";
};

/**
 * Get the status label based on the status
 * @param {string} status
 * @returns {string} status label
 */
export const getStatusLabel = (status) => {
  const statusItem = OFFER_STTATUS.find((item) => item.id === status);
  if (statusItem) {
    return statusItem.name;
  }
  return "Unknown";
};

/**
 * Custom hook that provides functionality for the dashboard component.
 *
 * This hook includes:
 * - Selectors to access the dashboard state.
 * - Dispatch to update the state.
 * - State management for the selected layout tab.
 * - Handlers for tab click events and filter tab changes.
 * - Functions to generate filter tabs and display layout tabs.
 *
 * @returns {Object} An object containing two functions:
 * - `getFilterTabs`: Generates the filter tabs for the dashboard.
 * - `getDisplayLayoutTab`: Generates the display layout tabs for the dashboard.
 */

const useDashboard = () => {
  // Selectors
  const dashboardSelector = useSelector((state: RootState) => state.dashboard);
  // Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { prepSteps } = useSolutionLoad();
  const authSelector = useSelector((state: RootState) => state.authSlice);

  const getMoreActions = (disableDuplicate = false,listView = false) => {
    const moreActionsCard = [
      {
        item_label: "Edit case",
        item_key: "edit_case",
        disabled: false,
        icon: <EditIcon size="16px" />,
      },
      {
        item_label: "Edit properties",
        item_key: "edit_properties",
        disabled: false,
        icon: <ListIcon size="16px" />,
      },
      {
        item_label: disableDuplicate && listView ? "Offer cannot be duplicated" : "Duplicate",
        item_key: "duplicate_clone_offer",
        disabled: disableDuplicate,
        icon: <DuplicateIcon size="16px" />,
      },
      {
        item_label: "Delete",
        item_key: "delete",
        disabled: false,
        icon: <DeleteIcon size="16px" />,
      },
    ];
    return moreActionsCard;
  }

  /**
   * Handles the actions for the offer.
   *
   * @param {Object} item - The action item.
   * @param {Object} offer - The selected offer.
   * @returns {void}
   * @example
   * handleOfferActions(item, offer);
   * */
  const handleOfferActions = (item, offer) => {
    dispatch(
      setProperty({
        key: "caseActions",
        value: { action: item.item_key, selectedOffer: offer },
      })
    );
  };

  /**
   * Handles the filter tab change.
   *
   * @param {string} tabId - The selected tab id.
   * @returns {void}
   * @example
   * handleFilterTbChange(tabId);
   * */
  const handleFilterTbChange = (tabId) => {
    dispatch(setProperty({ key: "filterTab", value: tabId }));
  };

  /**
   * Handles the display layout change.
   *
   * @param {string} tabId - The selected tab id.
   * @returns {void}
   * @example
   * handleDisplayChange(tabId);
   * */
  const handleDisplayChange = (tabId) => () => {
    dispatch(setProperty({ key: "viewType", value: tabId }));
  };

  /**
   * Generates the filter tabs for the dashboard.
   *
   * @returns {JSX.Element} A JSX element containing the filter tabs.
   */
  const getFilterTabs = () => {
    return (
      <Tabs alignment="left" fullWidth={true} variant="underlined">
        {FILTER_TOOLBAR_TABS.map((tab) => {
          return (
            <Tab
              id={`tab-${tab.id}`}
              role="option"
              key={`tab-${tab.id}`}
              selected={dashboardSelector.data.filterTab === tab.id}
              onSelect={() => handleFilterTbChange(tab.id)}
            >
              {tab.name}
            </Tab>
          );
        })}
      </Tabs>
    );
  };

  /**
   * Generates the layout tabs for the dashboard display.
   *
   * @returns {JSX.Element} A JSX element containing the layout tabs with icons.
   */
  const getDisplayLayoutTab = () => {
    return (
      <Tabs
        alignment="center"
        fullWidth={false}
        variant="icons"
        className="dsh-ft-view-tabs"
      //style={{ border: "solid 1px", justifyContent: "center",alignItems: "center" }}
      >
        {LAYOUT_TAB.map((tab) => {
          return (
            /** @ts-ignore */
            <Tab
              icon={true}
              tooltipProps={{
                tooltip: tab.name,
                placement: "bottom",
              }}
              id={`tab-${tab.id}`}
              role="option"
              key={`tab-${tab.id}`}
              selected={dashboardSelector.data?.["viewType"] === tab.id}
              onSelect={handleDisplayChange(tab.id)}
            >
              {/** @ts-ignore */}
              {tab.icon}
            </Tab>
          );
        })}
      </Tabs>
    );
  };


  /**
   * Returns the current dashboard grid columns configuration.
   */
  const getDashboardGidColumns = () => {
    return dashboardGidColumns;
  }

  /**
   * Generates the formatted grid data for the dashboard.
   *
   * @param {Array} data - The data to be displayed in the grid.
   * @param {boolean} isInlineRowActions - A flag to determine if inline row actions are enabled.
   * @returns {Object} An object containing the formatted grid data.
   */
  const getFormatedGridData = (data, isInlineRowActions) => {
    const gridFormatData = {
      label: "",
      id_key: "case_list",
      // isInlineRowActions: isInlineRowActions,
      overlayNoRowsTemplate: "No data available for the current selection",
      rowData: data,
      columnDefs: _.cloneDeep(dashboardGidColumns),
    };

    // Handle inline row actions
    if (isInlineRowActions) {
      // check if offer has temp_offer_id , if not then disable the duplicate action
      const setDuplicateActionDisable = data[0]?.temp_offer_id === "" || !data[0]?.temp_offer_id;
      const menuData = getMoreActions(setDuplicateActionDisable,true);
      const actionColoumn = {
        headerName: "",
        field: "",
        flex: 1,
        pinned: "right",
        cellRenderer: (params) => {
          return (
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <MoreActionButtons
                layout="horizontal"
                menuItems={menuData}
                onMenuClick={(item) => handleOfferActions(item, params.data)}
              />
            </div>
          );
        },
      };
      //@ts-ignore
      gridFormatData["columnDefs"].push(actionColoumn);
    }
    return gridFormatData;
  };

  /**
   * Processes the draft state of the selected offer.
   *
   * This function evaluates the selected offer's details and determines the appropriate
   * action based on its source, subtype, lead product, and status. Depending on the conditions,
   * it either retains the state and redirects to the Case page or redirects to the OOT (Offer
   * Optimization Tool). If the offer is not in a draft state, it may prompt the user to pull
   * the offer into a draft state before proceeding.
   *
   * @returns {void}
   * @example
   * processDraftState();
   */
  const processDraftState = (offerSelected=undefined) => {
    // Get the selected offer from the dashboard state
    const selectedOffer = offerSelected ? offerSelected : dashboardSelector.data?.caseActions?.["selectedOffer"];
    const { offer_id, source, subType, temp_offer_id, offerStatus, lead_product } = selectedOffer;
    const returnObject = {
      type: "RESTORE", // RESTORE/REDIRECT
      offer_id,
      message: "", // Message to show in the popup
      redirectUrl: "", // URL to redirect to
    }
    const ootUrl = `${process.env.REACT_APP_OOT}/offer/edit/${offer_id}`;

    
    if (source?.toLowerCase() === "cui" && subType?.toLowerCase() === "help") {
      // check if the offer is non-draft and user role is approver and user email is not in owner or co-owner
      const isApprover_redirectOOT  = checkRole_ownerDetails(selectedOffer);
      returnObject.message = "";
      returnObject.redirectUrl = "";

      if(isApprover_redirectOOT) {
        returnObject.type = "REDIRECT";
        returnObject.message = "As you are an approver and not the owner or co-owner of this offer, you will be redirected to OOT. !!";
        returnObject.redirectUrl = ootUrl;
        return returnObject;
      }

      // DOT Guided buying
      // Check Lead Product - NDAC / MPW
      if (lead_product?.toLowerCase() === 'mpw') {
        // MPW Product
        // Retain the state and redirect to Case page - last saved state
        returnObject.type = "RESTORE";
      }
      else {
        // Not MPW
        if (temp_offer_id === offer_id || offerStatus?.toLowerCase() === 'draft') {
          // Not submitted to OOT
          // Retain the state and redirect to Case page - last saved state
          returnObject.type = "RESTORE";
        }
        else {
          // Show message - popup to ask user to pull the offer to draft state and then redirect to OOT
          // Show warning meesage in popup
          returnObject.type = "REDIRECT";
          returnObject.message = `The offer (${offer_id}) is not in draft state , please make sure offer is moved back to draft state before editing offer. Click confirm button to redirect to offer repository page`;
          returnObject.redirectUrl = ootUrl; // URL to redirect to
        }
      }
    }
    else {
      // Redirect to OOT
      returnObject.type = "REDIRECT";
      returnObject.message = "";
      returnObject.redirectUrl = ootUrl; // URL to redirect to
    }
    return returnObject;
  }

  /**
   * Checks if the offer meets criteria for OOT redirect based on role and ownership.
   * Returns true if: offer is non-draft AND user role is approver AND user email is not in owner or co-owner
   *
   * @param {Object} selectedOffer - The selected offer object
   * @returns {boolean} True if should redirect to OOT, false otherwise
   */
  const checkRole_ownerDetails = (selectedOffer) => {
    const { offerStatus, owner, co_owner, owner_email } = selectedOffer;
    const owneremail = owner || owner_email;
    // Get current user details from auth state or context
    const userEmail = authSelector?.login?.mail || "";
    const isApprover = authSelector?.isUserApprover || "";
    // Check if offer is non-draft
    const isNonDraft = offerStatus?.toLowerCase() !== 'draft';
    
    // Check if user email is not in owner or co-owner
    const isNotOwner = owneremail !== userEmail;
    const isNotCoOwner = !_.includes(co_owner, userEmail);

    // Return true if all conditions are met (should redirect to OOT)
    return isNonDraft && isApprover && isNotOwner && isNotCoOwner;
  }

  /**
  * Safely parses a JSON string and returns the parsed object.
  * This function attempts to parse the input string as JSON. If the parsing fails,
  * it catches the error and returns null. If the parsed result is a string, it
  * attempts to parse it again to ensure the final result is an object.
  * @param {string} jsonString - The JSON string to be parsed.
  * @returns {Object|null} The parsed object or null if parsing fails.
  * 
  */
  const safeJsonParse = (jsonString) => {
    try {
      let parsed = JSON.parse(jsonString);
      // If it's a string again, parse it once more
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
        if (typeof parsed === 'string') {
          parsed = JSON.parse(parsed);
        }
      }
      return parsed;
    } catch (e) {
      //console.error("Invalid JSON", e);
      return null;
    }
  }

  /**
   * Restores the saved UI state for a given offer.
   *
   * This function retrieves the saved UI state of the specified offer from the backend
   * and updates the Redux store with the retrieved state. If no saved state is found,
   * an error message is dispatched to the store.
   *
   * @param {string | number} offer_id - The ID of the offer whose saved state is to be restored.
   * @returns {Promise<void>} A promise that resolves when the state restoration process is complete.
   * @example
   * await processSavedOfferState(12345);
   */
  const processSavedOfferState = async (offer_id,owner_email="") => {
    dispatch(setProperty({ key: "isLoaderDialogOpen", value: true }));
    const uiJson = await DATAMART_SERVICES.getSavedUiState(offer_id);
    dispatch(setProperty({ key: "isLoaderDialogOpen", value: false }));
    if (_.isEmpty(uiJson)) {
      // Show error meesage in popup
      dispatch(
        setError({
          key: "error",
          value: {
            errorCode: null,
            errorMsg: "There is no saved state for the selected offer - " + offer_id,
          },
        })
      );
    }
    else {
      // Check for the valid json string
      const jsonString = typeof uiJson === "string" ? uiJson : JSON.stringify(uiJson);
      const parsedJosn = safeJsonParse(jsonString);
      // Check if parsedJosn is valid
      if (!parsedJosn || typeof parsedJosn !== "object" || Array.isArray(parsedJosn)) {
        // Show error meesage in popup
        dispatch(
          setError({
            key: "error",
            value: {
              errorCode: null,
              errorMsg: "There is no valid saved state for the selected offer - " + offer_id,
            },
          })
        );
      }
      else {
        dispatch(setStateRestoreFlag({ isOfferDataRestored: true,owner_email }));
        // Check if data is migrated
        const isDataMigrated = parsedJosn?.theme?.data?.isDataMigrated || false;
        // If only elements are migrated, then delete theme from parsedJosn and set actionType to RESET_ONLY_ELEMENTS
        const isOnlyElementMigrated = parsedJosn?.theme?.data?.isElementMigrated || false;
        let actionType = "RESTORE_STORE";
        let resp = undefined;
        // If data is migrated, then fetch the segment form for the selected solution
        if (isDataMigrated || isOnlyElementMigrated) {
          const solution_selected = isDataMigrated ? parsedJosn?.config_stepper?.data?.solution_selected || undefined : parsedJosn?.theme?.data?.segment || undefined;
          if (solution_selected) {
            // Fetch the segment form for the selected solution
            resp = await CONFIG_API.getSegmentForm(solution_selected);            
            // Safely set packages, creating missing objects if needed
            setNestedValue(parsedJosn, "config_stepper.data.solution_form_data.packages", resp?.data?.packages || {});
          }
        }
        // If only elements are migrated, then delete theme from parsedJosn and set actionType to RESET_ONLY_ELEMENTS
        if (isOnlyElementMigrated) {
          // Delete theme from parsedJosn
          delete parsedJosn.theme;
          // Add dynamic.data.element
          // Clone dynamicInitialState to avoid mutating a frozen object
          setNestedValue(parsedJosn, "dynamic.data", _.cloneDeep(dynamicInitialState));
          parsedJosn.dynamic.data.elelment = parsedJosn?.element || {};
          // Delete element from parsedJosn
          delete parsedJosn?.element;
          // Delete modal if exists
          delete parsedJosn?.modal;

          // Prepare the stepper data and assign
          if(resp && resp?.data){
            const configSteps = prepSteps(resp.data);
            const configstepper = _.cloneDeep(initialData);
            configstepper.stepper_data = configSteps;            
            setNestedValue(parsedJosn, "config_stepper.data", configstepper);
            setNestedValue(parsedJosn, "config_stepper.data.solution_form_data.packages", resp?.data?.packages || {});
            //parsedJosn["config_stepper"] = configstepper;
            // Find the first step after "business" in configSteps
            const businessIndex = configSteps.findIndex(step => step.stepname === "business");
            let firstStepAfterBusiness = "business";
            if (businessIndex !== -1) {
              const firstStep = configSteps
              .slice(businessIndex + 1)
              .find(step => step.isDisabled !== true && step.isHidden !== true);
              firstStepAfterBusiness = firstStep?.stepname;
              // Set busoiness step to current
              configSteps.find(step => step.stepname === "business").status = 'completed';
              firstStep.status = 'current';
            }
            parsedJosn.config_stepper.data.active_step = firstStepAfterBusiness;
          }
        }
        // Dispatch the action to restore the store for restore the state
        dispatch({ type: actionType, payload: parsedJosn });
        navigate('/segment', { replace: true });
      }
    }
  }

  // Set a nested value in an object
  const setNestedValue = (obj, path, value) => {
    const keys = path.split('.');
    let currentObj = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      currentObj[key] ??= {}; // Create object if it doesn't exist
      currentObj = currentObj[key];
    }

    currentObj[keys[keys.length - 1]] = value;
    return currentObj;
  }

  /**
   * Handles the bookmarking of an offer by calling the DATAMART_API.
   *
   * @param {string} offerId - The ID of the offer to be bookmarked.
   * @param {string} actionType - The type of action to perform (e.g., "add" or "remove").
   * @returns {Promise<any>} - A promise that resolves to the bookmark data.
   * @throws Will dispatch an error action if the bookmarking process fails.
   */
  const handleOfferBookmark = async (offer_id: string | number, actionType: string) => {
    try {
      const bookmark = await DATAMART_API.handleBookmark(offer_id, actionType);
      // Show success message in popup
      if (bookmark && bookmark?.length > 0) {
        dispatch(
          setError({
            key: "info",
            value: {
              errorCode: null,
              errorMsg: "Bookmark updated successfully",
            },
          })
        );
      }
      return bookmark;
    } catch (e) {
      dispatch(
        setError({
          key: "warn",
          value: {
            errorCode: null,
            errorMsg: "Something went wrong while updating offer bookmark",
          },
        })
      );
    }
  }

  const resetPaginationToFirstPage = () => {
    const currentPage = dashboardSelector.data?.pagination['currentPage'];
    if (currentPage !== 1) {
      dispatch(
        setProperty({
          key: "pagination",
          value: {
            ...dashboardSelector.data?.pagination,
            currentPage: 1,
          },
        })
      );
    }
  }

  return {
    getDashboardGidColumns,
    handleOfferActions,
    getFilterTabs,
    getDisplayLayoutTab,
    getFormatedGridData,
    getStatusColor,
    processDraftState,
    processSavedOfferState,
    handleOfferBookmark,
    resetPaginationToFirstPage,
    safeJsonParse,
    getMoreActions
  };
};

export default useDashboard;
